import React from 'react';
import {useIntl} from 'react-intl';
import {useProjectTotals} from './ProjectScopingTotalsContext';
import {Title, Data, Element} from './ProjectScopingTotals_styled';
import {ValueCell} from '../../../../../components/initial-plan/ValueCell';
import {Tooltip} from '@forecast-it/design-system';
import {ActualHoursTooltip} from './ProjectScopingTotalsTooltips';

interface TotalsElementProps {
	totalsField: string;
	translationId: string;
	tooltipContent?: React.ReactNode;
}

const MoneyElement: React.FC<TotalsElementProps> = ({totalsField, translationId}) => {
	const intl = useIntl();
	const {totalsData, settings} = useProjectTotals();
	return (
		<Element column>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			<Data small>
				<ValueCell
					noJustify
					type={ValueCell.VALUE_TYPE.PRICE}
					value={totalsData[totalsField] || 0}
					currencySymbol={settings.currencySymbol}
				/>
			</Data>
		</Element>
	);
};

const TimeElement: React.FC<TotalsElementProps> = ({totalsField, translationId, tooltipContent}) => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();

	const DataComponent = ({showPointerOnHover}: {showPointerOnHover?: boolean}) => {
		return (
			<Data small showPointerOnHover={showPointerOnHover}>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.TIME} value={totalsData[totalsField] || 0} />
			</Data>
		);
	};

	return (
		<Element column>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			{tooltipContent ? (
				<Tooltip content={tooltipContent} interactable={false} width="xl">
					<DataComponent showPointerOnHover />
				</Tooltip>
			) : (
				<DataComponent />
			)}
		</Element>
	);
};

const PercentageElement: React.FC<TotalsElementProps> = ({totalsField, translationId}) => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<Element column>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.PERCENTAGE} value={totalsData[totalsField] || 0} />
			</Data>
		</Element>
	);
};

const ProjectProgressElement: React.FC = () => (
	<PercentageElement totalsField="progress" translationId="common.project_progress" />
);
const PlannedHoursElement: React.FC = () => {
	const {projectData, settings} = useProjectTotals();
	if (settings.useAllocationsForPlannedValues && projectData.hasAllocations)
		return <TimeElement totalsField="allocatedHours" translationId="project_budget.allocated_hours" />;
	if (projectData.useBaseline) return <TimeElement totalsField="baselineHours" translationId="baseline.hours" />;
	return <TimeElement totalsField="estimatedHours" translationId="common.estimated_hours" />;
};

const ActualHoursElement: React.FC = () => {
	const {settings} = useProjectTotals();

	return settings.useAllocationsForActualValues ? (
		<TimeElement totalsField="actualHours" translationId={'project_settings.financials.value_calculations.actual_hours'} />
	) : (
		<TimeElement
			totalsField="actualHours"
			translationId={'project_settings.financials.value_calculations.actual_hours'}
			tooltipContent={<ActualHoursTooltip />}
		/>
	);
};

const RemainingWorkHoursElement: React.FC = () => (
	<TimeElement totalsField="remainingHours" translationId="common.remaining_work_hours" />
);

const ProjectedTotalHoursElement: React.FC = () => (
	<TimeElement totalsField="projectedTotalHours" translationId="common.projected_total_hours" />
);

const EstimatedCostElement: React.FC = () => <MoneyElement totalsField="plannedCost" translationId="common.estimated_cost" />;

const BaselineCostElement: React.FC = () => <MoneyElement totalsField="baselineCost" translationId="common.baseline_cost" />;

const PlannedCostElement: React.FC = () => {
	const {projectData, settings} = useProjectTotals();
	if (settings.useAllocationsForPlannedValues && projectData.hasAllocations) return <EstimatedCostElement />;
	if (projectData.useBaseline) return <BaselineCostElement />;
	return <EstimatedCostElement />;
};

const ActualCostElement: React.FC = () => <MoneyElement totalsField="actualCost" translationId="common.actual_cost" />;

const ProjectedTotalCostElement: React.FC = () => (
	<MoneyElement totalsField="projectedTotalCost" translationId="common.projected_total_cost" />
);

const ProjectedTotalRevenueElement: React.FC = () => (
	<MoneyElement totalsField="projectedTotalRevenue" translationId="common.projected_total_revenue" />
);

const ProjectedTotalProfitElement: React.FC = () => (
	<MoneyElement totalsField="projectedTotalProfit" translationId="common.projected_total_profit" />
);

const ProjectedTotalMarginElement: React.FC = () => (
	<PercentageElement totalsField="projectedTotalMargin" translationId="common.projected_total_margin" />
);

export const ProjectScopingTotalsElements = {
	ProjectProgress: ProjectProgressElement,
	PlannedHours: PlannedHoursElement,
	ActualHours: ActualHoursElement,
	RemainingWorkHours: RemainingWorkHoursElement,
	ProjectedTotalHours: ProjectedTotalHoursElement,
	EstimatedCost: EstimatedCostElement,
	BaselineCost: BaselineCostElement,
	PlannedCost: PlannedCostElement,
	ActualCost: ActualCostElement,
	ProjectedTotalCost: ProjectedTotalCostElement,
	ProjectedTotalRevenue: ProjectedTotalRevenueElement,
	ProjectedTotalProfit: ProjectedTotalProfitElement,
	ProjectedTotalMargin: ProjectedTotalMarginElement,
};
